import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { useAuth } from "../../context/AuthContext.jsx";
import {
  LAND_DEAL,
  RENTAL_PROPERTY,
  HOUSEHACK_PROPERTY,
  CREDIT_TIERS,
  mortgagePayment,
  sumEffects,
} from "./data.js";
import Celebration from "../../components/Celebration.jsx";

const LEVEL_ID = "property-tycoon";

function formatMoney(n) {
  return Math.round(n).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

function computeLandOutcome(plan, log) {
  const forcedCost = log.find((l) => l.choice.effect?.forceFinalCost != null);
  const finalCost = forcedCost ? forcedCost.choice.effect.forceFinalCost : plan.offer;
  const oneTimeCosts = sumEffects(log, "oneTimeCost");
  const exitEntry = log.find((l) => l.choice.effect?.exitStrategy);
  const exitStrategy = exitEntry ? exitEntry.choice.effect.exitStrategy : "cash";

  let gross;
  let outcomeLabel;
  if (exitStrategy === "financing") {
    const monthlyPayment = 200; // illustrative owner-financing payment
    gross = monthlyPayment * LAND_DEAL.financingTermYears * 12;
    outcomeLabel = `${formatMoney(monthlyPayment)}/mo for ${LAND_DEAL.financingTermYears} years`;
  } else {
    gross = Math.round(LAND_DEAL.marketValue * 0.85); // quick cash sale, below full value
    outcomeLabel = "Cash sale";
  }

  const profit = gross - finalCost - oneTimeCosts;
  return {
    headline: profit,
    headlineLabel: "Total Profit",
    detail: outcomeLabel,
    rows: [
      ["Final Acquisition Cost", formatMoney(finalCost)],
      ["Gross Proceeds", formatMoney(gross)],
      ["One-Time Costs", formatMoney(oneTimeCosts)],
    ],
    financialBonus: Math.max(0, Math.min(400, Math.round(profit / 50))),
  };
}

function computeRentalOutcome(plan, log) {
  const tier = CREDIT_TIERS.find((t) => t.id === plan.creditTierId);
  const rateBump = sumEffects(log, "rateBump");
  const loanAmount = RENTAL_PROPERTY.price * (1 - plan.downPaymentPct / 100);
  const mortgage = mortgagePayment(loanAmount, tier.rate + rateBump);
  const monthlyDelta = sumEffects(log, "monthlyDelta");
  const oneTimeCosts = sumEffects(log, "oneTimeCost");

  const monthlyCashFlow = RENTAL_PROPERTY.marketRent - RENTAL_PROPERTY.fixedMonthlyCosts - mortgage + monthlyDelta;
  const annualCashFlow = monthlyCashFlow * 12 - oneTimeCosts;

  return {
    headline: monthlyCashFlow,
    headlineLabel: "Monthly Cash Flow",
    detail: `${tier.label} credit @ ${(tier.rate + rateBump).toFixed(1)}% · ${plan.downPaymentPct}% down`,
    rows: [
      ["Est. Mortgage", formatMoney(mortgage) + "/mo"],
      ["Year 1 Net Cash Flow", formatMoney(annualCashFlow)],
      ["One-Time Costs", formatMoney(oneTimeCosts)],
    ],
    financialBonus: Math.max(0, Math.min(400, Math.round(monthlyCashFlow))),
  };
}

function computeHouseHackOutcome(_plan, log) {
  const monthlyDelta = sumEffects(log, "monthlyDelta");
  const oneTimeCosts = sumEffects(log, "oneTimeCost");
  const loanAmount = HOUSEHACK_PROPERTY.price * (1 - HOUSEHACK_PROPERTY.downPaymentPct / 100);
  const mortgage = mortgagePayment(loanAmount, HOUSEHACK_PROPERTY.rate);

  const monthlyHousingCost =
    mortgage + HOUSEHACK_PROPERTY.fixedMonthlyCosts - HOUSEHACK_PROPERTY.otherUnitRent - monthlyDelta;
  const savingsVsRenting = HOUSEHACK_PROPERTY.comparisonRent - monthlyHousingCost;
  const annualSavings = savingsVsRenting * 12 - oneTimeCosts;

  return {
    headline: savingsVsRenting,
    headlineLabel: "Monthly Savings vs. Renting",
    detail: monthlyHousingCost <= 0 ? "Living cost-free, with cash left over" : `${formatMoney(monthlyHousingCost)}/mo out of pocket`,
    rows: [
      ["Est. Mortgage", formatMoney(mortgage) + "/mo"],
      ["Annual Savings", formatMoney(annualSavings)],
      ["One-Time Costs", formatMoney(oneTimeCosts)],
    ],
    financialBonus: Math.max(0, Math.min(400, Math.round(savingsVsRenting))),
  };
}

function computeOutcome(reconResult, plan, campaignResult) {
  const { log } = campaignResult;
  const pathOutcome =
    plan.path === "land"
      ? computeLandOutcome(plan, log)
      : plan.path === "rental"
      ? computeRentalOutcome(plan, log)
      : computeHouseHackOutcome(plan, log);

  const goodChoices = log.filter((l) => l.choice.tag === "good").length;
  const badChoices = log.filter((l) => l.choice.tag === "bad").length;

  const reconScore = reconResult.correctCount * 15;
  const choiceScore = goodChoices * 20 - badChoices * 20;
  const score = Math.max(50, reconScore + choiceScore + pathOutcome.financialBonus + 50);

  return { ...pathOutcome, goodChoices, badChoices, score };
}

const PATH_TITLES = { land: "Land Flip", rental: "Rental Property", househack: "House Hack" };

export default function DebriefStage({ reconResult, plan, campaignResult, onRetry }) {
  const navigate = useNavigate();
  const { user, profile, refreshProfile } = useAuth();
  const [saving, setSaving] = useState(true);
  const [saved, setSaved] = useState(false);

  const outcome = computeOutcome(reconResult, plan, campaignResult);
  const didLevelUp = Math.floor(((profile?.xp ?? 0) + outcome.score) / 500) + 1 > (profile?.level ?? 1);

  useEffect(() => {
    let cancelled = false;

    async function save() {
      if (!user) {
        setSaving(false);
        return;
      }
      const newXp = (profile?.xp ?? 0) + outcome.score;
      const newLevel = Math.max(profile?.level ?? 1, Math.floor(newXp / 500) + 1);
      const newNetWorth = (profile?.net_worth_cents ?? 0) + outcome.score * 100;

      const { error } = await supabase
        .from("profiles")
        .update({ xp: newXp, level: newLevel, net_worth_cents: newNetWorth })
        .eq("id", user.id);

      if (!cancelled) {
        if (!error) {
          await refreshProfile();
          const { error: challengeError } = await supabase.rpc("record_level_result", {
            p_level_id: LEVEL_ID,
            p_score: outcome.score,
          });
          if (challengeError) {
            console.error("Failed to record challenge result:", challengeError.message);
          }
          setSaved(true);
        } else {
          console.error("Failed to save Property Tycoon results:", error.message);
        }
        setSaving(false);
      }
    }

    save();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="animate-popIn text-center">
      <Celebration show={saved} levelUp={didLevelUp} />
      <h2 className="pixel-heading mb-1 text-lg text-lime">DEAL DEBRIEF</h2>
      <p className="mb-6 text-xs uppercase tracking-widest text-mist/50">{PATH_TITLES[plan.path]}</p>

      <div className="mx-auto mb-4 max-w-sm border-2 border-gold bg-gold/10 p-5">
        <p className="label-pixel mb-1 text-gold">{outcome.headlineLabel}</p>
        <p className={`font-display text-2xl ${outcome.headline >= 0 ? "text-gold" : "text-danger"}`}>
          {formatMoney(outcome.headline)}
        </p>
        <p className="mt-2 text-[11px] text-mist/60">{outcome.detail}</p>
      </div>

      <div className="mx-auto mb-6 max-w-sm space-y-2 border-2 border-black bg-ink p-4 text-left text-sm">
        <div className="flex justify-between">
          <span className="text-mist/60">Recon Accuracy</span>
          <span className="text-cyan">{reconResult.correctCount}/6</span>
        </div>
        <div className="flex justify-between">
          <span className="text-mist/60">Good Calls / Rough Calls</span>
          <span>
            <span className="text-lime">{outcome.goodChoices}</span>
            <span className="text-mist/40"> / </span>
            <span className="text-danger">{outcome.badChoices}</span>
          </span>
        </div>
        {outcome.rows.map(([label, value]) => (
          <div key={label} className="flex justify-between text-[11px] text-mist/50">
            <span>{label}</span>
            <span>{value}</span>
          </div>
        ))}
        <div className="relative flex justify-between border-t border-black/50 pt-2 font-display text-[11px]">
          <span className="text-mist/60">SCORE</span>
          <span className="text-gold">{outcome.score.toLocaleString()}</span>
          {saved && (
            <span className="absolute -top-4 right-0 animate-floatUp text-[9px] text-lime">+{outcome.score} XP</span>
          )}
        </div>
      </div>

      <p className="mb-6 text-xs text-mist/50">
        {saving ? "Saving to your profile…" : saved ? "Net worth and XP updated on your profile ✅" : "Results ready."}
      </p>

      <div className="flex flex-wrap justify-center gap-3">
        <button onClick={onRetry} className="btn-magenta">
          ↻ TRY ANOTHER PATH
        </button>
        <button onClick={() => navigate("/")} className="btn-cyan">
          LEVEL SELECT
        </button>
      </div>
    </div>
  );
}
